import {
  ChevronDownIcon,
  CircleCheckIcon,
  Typography,
  textLinkStyles,
} from "@codat/orchard-ui";
import classNames from "classnames";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import { SourceTypeStatus, useSourceTypes } from "contexts/SourceTypesContext";
import { useGATrackingEvent } from "hooks/useGATracking";
import useNextSourceTypeRedirectHandler from "hooks/useNextSourceTypeRedirectHandler";
import useQueryParams from "hooks/useQueryParams";
import { useSourceTypeDisplayProps } from "hooks/useSourceTypeDisplayProps";
import { useSourceTypeResolver } from "hooks/useSourceTypeResolver";
import { useMemo, useState } from "react";
import {
  Link,
  RouteComponentProps,
  useHistory,
  useLocation,
} from "react-router-dom";
import { formatUrl, usePageViewedEvent } from "utils";

import ConnectionComplete from "components/ConnectionComplete";
import ExitModal from "components/ExitModal";
import LinkPageRoot from "components/LinkPageRoot";
import {
  WithSourceTypeEvent,
  WithSourceTypeInnerProps,
} from "components/WithSourceTypeEventContext";
import { CompanySourceTypeParams, routes } from "config/urls";

import styles from "./CompanyRevisit.module.css";

type CompanyRevisitProps = RouteComponentProps<CompanySourceTypeParams> &
  WithSourceTypeInnerProps;

function CompanyRevisitContent({ match }: CompanyRevisitProps) {
  const { state } = useConfig();
  const { sourceTypes, status: sourceTypeStatus } = useSourceTypes();
  const queryParams = useQueryParams();
  const history = useHistory();
  const { logGAEvent } = useGATrackingEvent();
  const isComplete = useMemo(
    () =>
      sourceTypeStatus === SourceTypeStatus.READY &&
      state.status === ConfigStatusType.READY,
    [state.status, sourceTypeStatus]
  );

  const [isExitModal, setExitModal] = useState(false);

  const { currentSourceType, nextPrioritySourceType } = useSourceTypeResolver(
    match.params,
    sourceTypes
  );

  usePageViewedEvent({
    ready: !!currentSourceType?.sourceType && isComplete,
    clientConfig: state.data,
    sourceTypes,
  });

  const displayProps = useSourceTypeDisplayProps(currentSourceType);

  const currentQueryString = useLocation().search;
  const overviewPageUrl = `${formatUrl(routes.company.overview, {
    companyId: match.params.companyId,
  })}${currentQueryString ? currentQueryString : ""}`;

  const handleExitButtonClick = state.data.hasCustomRedirect
    ? () => {
        logGAEvent("Link Flow", "ExitIcon", match.params.companyId);
        setExitModal(true);
      }
    : undefined;

  let handleNextButtonClick: (() => void) | undefined;

  const nextPrioritySourceTypeRedirect = useNextSourceTypeRedirectHandler(
    match.params.companyId,
    nextPrioritySourceType
  );

  if (nextPrioritySourceType) {
    handleNextButtonClick = nextPrioritySourceTypeRedirect;
  } else {
    handleNextButtonClick = () => history.push(overviewPageUrl);
  }

  return (
    <>
      <LinkPageRoot
        isComplete={isComplete}
        showLinkedAccounts
        onExitClick={handleExitButtonClick}
        primaryCtaProps={
          handleNextButtonClick
            ? {
                onClick: handleNextButtonClick,
                disabled: !sourceTypes,
                analyticsName: "Next",
              }
            : undefined
        }
        currentSourceType={currentSourceType}
      >
        {currentSourceType ? (
          <ConnectionComplete
            Icon={(p: { className: string }) => (
              <CircleCheckIcon
                className={classNames(p.className, styles.icon)}
              />
            )}
            title={`${currentSourceType.sourceType} software connected`}
            className={styles.container}
          >
            <div className={styles.subText}>
              <Typography as="p">{displayProps.revisit.message}</Typography>
              <Link
                to={formatUrl<CompanySourceTypeParams>(
                  displayProps.revisit.linkUrl,
                  {
                    companyId: match.params.companyId,
                    sourceType: match.params.sourceType,
                  },
                  Object.fromEntries(queryParams.entries())
                )}
                className={textLinkStyles}
                style={{ color: state.data.colors?.primary }}
                data-testid="company-revisit-make-changes-link"
              >
                <strong>{displayProps.revisit.linkText}</strong>
                <ChevronDownIcon className={styles.linkIcon} />
              </Link>
            </div>
          </ConnectionComplete>
        ) : (
          <div data-testid="revist-source-types-loading">Loading...</div>
        )}
      </LinkPageRoot>
      {state.data.hasCustomRedirect && currentSourceType && (
        <ExitModal
          isOpen={isExitModal}
          onClose={() => setExitModal(false)}
          companyId={match.params.companyId}
          sourceType={currentSourceType.sourceType}
        ></ExitModal>
      )}
    </>
  );
}

export const CompanyRevisit: React.FC<
  RouteComponentProps<CompanySourceTypeParams>
> = (props) => {
  return (
    <WithSourceTypeEvent
      sourceType={props.match.params?.sourceType}
      eventName={(sourceType) => `Link ${sourceType} revisit page`}
    >
      {(sourceTypeProps) => (
        <CompanyRevisitContent {...sourceTypeProps} {...props} />
      )}
    </WithSourceTypeEvent>
  );
};
